import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home testimonial — clean rewrite of legacy molecules/HomeNewTestimonial
// (Strapi sections.home-new-testiomonial + components.testimonial-cards).
// Legacy used react-slick; this is a self-contained carousel: a translated
// track + prev/next + dots, active index via client useState (SSR renders
// slide 0). No styled-system / next/image / react-slick.
export const homeTestimonialSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  cards: z
    .array(
      z.object({
        review: z.string().min(1),
        name: z.string().min(1),
        designation: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
    )
    .min(1)
    .max(20),
});

type HomeTestimonialProps = z.infer<typeof homeTestimonialSchema>;

// Fixed card geometry so the vertical track can translate by a known step
// (SSR-safe — no runtime measurement) and the next card "peeks" below.
const CARD_H = 40; // rem
const CARD_GAP = 2.4; // rem
const STEP = CARD_H + CARD_GAP;

const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;

  @media (min-width: 64em) {
    padding: 10rem 1.6rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3.2rem;

  @media (min-width: 64em) {
    grid-template-columns: minmax(28rem, 40%) 1fr;
    gap: 6rem;
    align-items: center;
  }
`;

const Left = styled.div`
  text-align: center;

  @media (min-width: 64em) {
    text-align: left;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 1.2rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.8rem, 4vw, 5.6rem);
  line-height: 1.1;
`;

const Progress = styled.div`
  margin: 3.2rem auto 0;
  width: min(36rem, 100%);
  height: 0.6rem;
  border-radius: 999px;
  background: #dbe4f5;
  overflow: hidden;

  @media (min-width: 64em) {
    margin-left: 0;
    margin-right: 0;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #12256b;
  transition: width 0.4s ease;
`;

const Controls = styled.div`
  display: flex;
  gap: 1.6rem;
  margin-top: 3.2rem;
  justify-content: center;

  @media (min-width: 64em) {
    justify-content: flex-start;
  }
`;

const NavButton = styled.button<{ $primary?: boolean }>`
  appearance: none;
  width: 6.4rem;
  height: 6.4rem;
  border: none;
  border-radius: 1.4rem;
  display: grid;
  place-items: center;
  cursor: pointer;
  color: #ffffff;
  background: ${({ $primary }) => ($primary ? '#12256b' : '#9fabd8')};
  transition: opacity 0.2s ease, background 0.2s ease;

  svg {
    width: 2.2rem;
    height: 2.2rem;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Viewport = styled.div`
  overflow: hidden;
  height: ${CARD_H + 6}rem;
`;

const Track = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${CARD_GAP}rem;
  transition: transform 0.5s ease;
`;

const Card = styled.figure<{ $active: boolean }>`
  flex: 0 0 ${CARD_H}rem;
  height: ${CARD_H}rem;
  box-sizing: border-box;
  margin: 0;
  padding: 4rem;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8efff;
  border-radius: 2.4rem;
  background: #ffffff;
  box-shadow: 0 2.4rem 5rem -3rem rgba(6, 48, 145, 0.35);
  opacity: ${({ $active }) => ($active ? 1 : 0.55)};
  transition: opacity 0.4s ease;
`;

const QuoteMark = styled.span`
  color: #12256b;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 6rem;
  line-height: 0.4;
  height: 3.2rem;
`;

const Quote = styled.blockquote`
  margin: 1.6rem 0 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-size: clamp(1.6rem, 1.6vw, 2rem);
  line-height: 1.6;
  overflow: auto;
  flex: 1 1 auto;
`;

const Person = styled.figcaption`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  margin-top: 2.4rem;
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 6rem;
  height: 6rem;
  border-radius: 1.2rem;
  object-fit: cover;
`;

const Name = styled.p`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 1.8rem;
`;

const Designation = styled.p`
  margin: 0.2rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
`;

const ArrowUp = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 19V5M12 5l-6 6M12 5l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowDown = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M12 19l-6-6M12 19l6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function HomeTestimonial({ eyebrow, heading, cards }: HomeTestimonialProps) {
  const [active, setActive] = useState(0);
  const last = cards.length - 1;
  const go = (i: number) => setActive(Math.max(0, Math.min(last, i)));
  const multi = cards.length > 1;

  return (
    <Section>
      <Grid>
        <Left>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          <Heading>{heading}</Heading>
          {multi ? (
            <>
              <Progress>
                <ProgressFill style={{ width: `${((active + 1) / cards.length) * 100}%` }} />
              </Progress>
              <Controls>
                <NavButton onClick={() => go(active - 1)} disabled={active === 0} aria-label="Previous testimonial">
                  {ArrowUp}
                </NavButton>
                <NavButton $primary onClick={() => go(active + 1)} disabled={active === last} aria-label="Next testimonial">
                  {ArrowDown}
                </NavButton>
              </Controls>
            </>
          ) : null}
        </Left>

        <Viewport>
          <Track style={{ transform: `translateY(calc(-${STEP}rem * ${active}))` }}>
            {cards.map((card, i) => (
              <Card key={`${card.name}-${i}`} $active={i === active} aria-hidden={i !== active}>
                <QuoteMark aria-hidden="true">&#8220;</QuoteMark>
                <Quote>{card.review}</Quote>
                <Person>
                  {card.imageUrl ? <Avatar src={card.imageUrl} alt={card.name} /> : null}
                  <span>
                    <Name>{card.name}</Name>
                    {card.designation ? <Designation>{card.designation}</Designation> : null}
                  </span>
                </Person>
              </Card>
            ))}
          </Track>
        </Viewport>
      </Grid>
    </Section>
  );
}

export const homeTestimonialBlock: BlockDefinition<HomeTestimonialProps> = {
  type: 'homeTestimonial',
  schema: homeTestimonialSchema,
  Renderer: HomeTestimonial,
};
